import { Injector } from '@angular/core';

import { AbstractFieldsModel } from './abstractFieldsModel';
import { FormConfig, FormField, Module, PropertyService } from '@services';
import { PropertyPickerComponent } from '@modules/management/modules/property-picker/components';
import { IProperty, PropertySource, PropertySubject } from '@services/property/property-model.interfaces';
import { QuestionFormService } from '@modules/management/pages/details/check/services/question/question-form.service';
import { ListColumn, PropertyPickerConfig, SelectedProperty } from '@modules/management/modules/property-picker/model/property-picker.interfaces';

import { cloneDeep, includes } from 'lodash';
import { TranslateService } from '@ngx-translate/core';

export class PropertyFieldsModel extends AbstractFieldsModel {
  private translate: TranslateService = this.injector.get(TranslateService);
  private propertyService: PropertyService = this.injector.get(PropertyService);

  protected formConfig: FormConfig = {
    containers: true,
    prefix: 'property',
    fields: [
      {
        containerClass: 'question-field',
        inputtype: 'verbatim',
        name: 'label',
        title: 'MGMT_DETAILS.Field_Label',
        type: 'text',
        size: 20,
        required: true
      },
      {
        containerClass: 'question-field',
        inputtype: 'verbatim',
        name: 'translation_label',
        title: this.translate.instant('MGMT_DETAILS.Field_Label_Translations'),
        type: 'textTranslations',
        size: 20,
      },
      {
        containerClass: 'question-field',
        break: true,
        role: 'none',
        title: 'MGMT_DETAILS.Field_Options',
        name: 'fieldOptions',
        type: 'checkbox',
        class: 'inline-checkboxes',
        options: [
          {
            id: 'required',
            description: 'SHARED.Required'
          },
          {
            id: 'barcode',
            description: 'SHARED.Barcode'
          },
          {
            id: 'multiple',
            description: 'SHARED.Multiple'
          }
        ],
        onChange: (value, $form: JQuery) => {
          this.markFieldAsChanged($form.attr('id'), 'fieldOptions', value);
        }
      }
    ]
  };
  private fields = [
    {
      containerClass: 'question-type-field property',
      title: 'MGMT_DETAILS.Question_Input_Details',
      type: 'subtitle'
    }
  ];

  constructor(
    protected questionFormService: QuestionFormService,
    protected injector: Injector
  ) {
    super(questionFormService);
  }

  public getFields(): FormField[] {
    const subject = this.questionFormService.getPropertySubject();
    const fields: FormField[] = cloneDeep(this.fields);
    const titleMap = {
      [PropertySubject.Asset]: 'MGMT_LIST.Asset_Property'
    };

    if (subject) {
      fields.push({
        containerClass: 'question-field question-type-field property',
        type: 'customElement',
        required: true,
        name: 'selectedProperty',
        component: PropertyPickerComponent,
        module: Module.PROPERTIES,
        inputs: {
          pickerConfig: <PropertyPickerConfig>{
            type: subject,
            selectedList: {
              columns: [
                ListColumn.FullPropertyName
              ],
              draggable: false,
              title: titleMap[subject]
            },
            viewTree: {
              filter(property: IProperty) {
                return includes([PropertySource.Hybrid, PropertySource.Manual], property.source);
              },
              multiselect: false,
              disableParentSelection: true
            }
          }
        },
        hideFieldsOnValue: {
          select: [
            {
              hideClasses: ['healthRelated'],
              valueFunction: (properties: SelectedProperty[]) => {
                const propertyId = properties?.[0]?.propertyID;

                if (propertyId) {
                  const property = this.propertyService.getPropertyById(propertyId);
                  return !property?.healthRelated;
                }

                return true;
              }
            },
          ]
        }
      },
      {
        containerClass: 'question-field question-type-field property healthRelated',
        title: 'MGMT_DETAILS.Question_Health_Related',
        name: 'healthRelated',
        type: 'flipswitch',
        onText: this.translate.instant('SHARED.On'),
        offText: this.translate.instant('SHARED.Off'),
        value: 1,
        default: 0
      });
    }

    return fields;
  }

  public getData(): any[] {
    const settingsHandler = (settingsItem: any) => {
      return settingsItem;
    };

    return this.getFormData([], settingsHandler);
  }
}
